
import React, { useMemo } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import type { MortgageData } from '../../CalculatorTabs';
import { Info, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PaymentBreakdownTabProps {
  selectedTerm: string;
  mortgageData: MortgageData;
}

interface BreakdownItem {
  name: string;
  value: number;
  color: string;
  category: 'loan' | 'escrow' | 'insurance' | 'fees';
  description: string;
}

export const PaymentBreakdownTab: React.FC<PaymentBreakdownTabProps> = ({ selectedTerm, mortgageData }) => {
  const paymentBreakdownData = useMemo(() => {
    const { monthlyPayment } = mortgageData;
    
    const breakdown: BreakdownItem[] = [
      { 
        name: 'Principal', 
        value: Math.round(monthlyPayment.principal), 
        color: '#3b82f6', 
        category: 'loan',
        description: 'Amount that goes toward paying down your loan balance'
      },
      { 
        name: 'Interest', 
        value: Math.round(monthlyPayment.interest), 
        color: '#8b5cf6', 
        category: 'loan',
        description: 'Cost of borrowing money from the lender'
      },
      { 
        name: 'Property Taxes', 
        value: Math.round(monthlyPayment.taxes), 
        color: '#ef4444', 
        category: 'escrow',
        description: 'Monthly property tax payment held in escrow'
      },
      { 
        name: 'Home Insurance', 
        value: Math.round(monthlyPayment.insurance), 
        color: '#f59e0b', 
        category: 'escrow',
        description: 'Monthly homeowners insurance premium'
      },
    ];
    
    // Only include PMI if it exists (depends on down payment %)
    if (monthlyPayment.pmi > 0) {
      breakdown.push({ 
        name: 'PMI', 
        value: Math.round(monthlyPayment.pmi), 
        color: '#ec4899', 
        category: 'insurance',
        description: 'Private Mortgage Insurance required for down payments less than 20%'
      });
    }
    
    // Only include HOA if user entered it
    if (monthlyPayment.hoa > 0) {
      breakdown.push({ 
        name: 'HOA Fees', 
        value: Math.round(monthlyPayment.hoa), 
        color: '#06b6d4', 
        category: 'fees',
        description: 'Monthly homeowners association fees'
      });
    }
    
    return breakdown;
  }, [mortgageData]);

  const insights = useMemo(() => {
    const result = [];
    const downPaymentPercent = (mortgageData.downPayment / mortgageData.homePrice) * 100;
    
    // PMI insights
    if (mortgageData.monthlyPayment.pmi > 0) {
      result.push({
        icon: AlertCircle,
        text: `PMI required: Down payment is ${downPaymentPercent.toFixed(1)}% (less than 20%)`,
        color: 'text-amber-400'
      });
    } else if (downPaymentPercent >= 20 && mortgageData.loanType === 'conventional') {
      result.push({
        icon: CheckCircle,
        text: 'No PMI - Your 20%+ down payment saves you money!',
        color: 'text-green-400'
      });
    }
    
    // VA loan insights
    if (mortgageData.loanType === 'va') {
      result.push({
        icon: Shield,
        text: 'VA loan - No PMI required regardless of down payment',
        color: 'text-blue-400'
      });
    }
    
    // FHA insights
    if (mortgageData.loanType === 'fha') {
      result.push({
        icon: Info,
        text: 'FHA loans include Mortgage Insurance Premium (MIP) for the life of the loan',
        color: 'text-purple-400'
      });
    }
    
    return result;
  }, [mortgageData]);

  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-6 space-y-6">
      <div>
        <h3 className="text-lg font-bold text-slate-100 mb-2">Monthly Payment Breakdown</h3>
        <p className="text-sm text-slate-400">Detailed breakdown of your estimated monthly payment based on {selectedTerm}-year term</p>
      </div>

      {/* Total Payment Display */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-slate-200">Total Monthly Payment</span>
          <span className="text-3xl font-bold text-white">
            {formatCurrency(mortgageData.monthlyPayment.total)}
          </span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Pie Chart */}
        <div className="w-full lg:w-80 h-80 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={paymentBreakdownData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                innerRadius={60}
                dataKey="value"
                stroke="#1e293b"
                strokeWidth={2}
              >
                {paymentBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0f172a', 
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#f1f5f9',
                    padding: '8px 12px'
                  }}
                  itemStyle={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                  labelStyle={{
                    color: '#f1f5f9',
                    fontWeight: '500',
                    fontSize: '13px',
                    marginBottom: '4px'
                  }}
                  cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                  formatter={(value) => [formatCurrency(Number(value)), '']}
                />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Detailed Breakdown */}
        <div className="flex-1 w-full">
          <TooltipProvider>
            <div className="space-y-3">
              {paymentBreakdownData.map((item, index) => {
                const percentage = (item.value / mortgageData.monthlyPayment.total) * 100;
                return (
                  <div key={index} className="flex justify-between items-center border-b border-slate-700 pb-3 hover:bg-slate-800/30 px-2 py-2 rounded transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: item.color }}
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-slate-300 font-semibold">{item.name}</span>
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-slate-500 hover:text-slate-300 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-sm">{item.description}</p>
                          </TooltipContent>
                        </UITooltip>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-100 font-bold text-lg">
                        {formatCurrency(item.value)}
                      </div>
                      <div className="text-xs text-slate-400 font-medium">
                        {percentage.toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TooltipProvider>

          {/* Category Summary */}
          <div className="mt-6 pt-6 border-t border-slate-700">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">Payment Categories</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-800/40 rounded-lg p-3">
                <div className="text-xs text-slate-400 mb-1">Loan Payment</div>
                <div className="text-lg font-bold text-blue-400">
                  {formatCurrency(paymentBreakdownData
                    .filter(item => item.category === 'loan')
                    .reduce((sum, item) => sum + item.value, 0))}
                </div>
              </div>
              <div className="bg-slate-800/40 rounded-lg p-3">
                <div className="text-xs text-slate-400 mb-1">Escrow Items</div>
                <div className="text-lg font-bold text-red-400">
                  {formatCurrency(paymentBreakdownData
                    .filter(item => item.category === 'escrow')
                    .reduce((sum, item) => sum + item.value, 0))}
                </div>
              </div>
              {paymentBreakdownData.some(item => item.category === 'insurance') && (
                <div className="bg-slate-800/40 rounded-lg p-3">
                  <div className="text-xs text-slate-400 mb-1">Insurance</div>
                  <div className="text-lg font-bold text-pink-400">
                    {formatCurrency(paymentBreakdownData
                      .filter(item => item.category === 'insurance')
                      .reduce((sum, item) => sum + item.value, 0))}
                  </div>
                </div>
              )}
              {paymentBreakdownData.some(item => item.category === 'fees') && (
                <div className="bg-slate-800/40 rounded-lg p-3">
                  <div className="text-xs text-slate-400 mb-1">Other Fees</div>
                  <div className="text-lg font-bold text-cyan-400">
                    {formatCurrency(paymentBreakdownData
                      .filter(item => item.category === 'fees')
                      .reduce((sum, item) => sum + item.value, 0))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Insights Section */}
      {insights.length > 0 && (
        <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Payment Insights</h4>
          {insights.map((insight, index) => {
            const Icon = insight.icon;
            return (
              <div key={index} className="flex items-start gap-3">
                <Icon className={`w-5 h-5 ${insight.color} flex-shrink-0 mt-0.5`} />
                <p className="text-sm text-slate-300">{insight.text}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
